import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Badge,
} from '../../../design-system/components';
import { cn } from '../../../design-system/utils/cn';
import { Check, Sun, Moon } from 'lucide-react';
import { admin_settings_theme_settings_path } from '../../../lib/routes';

// Default CSS values (from application.css @theme) for when a theme doesn't override a token
const DEFAULTS = {
  'primary-500': '#fb2f57',
  'primary-300': '#ff7a91',
  'surface': '#ffffff',
  'surface-secondary': '#fafafa',
  'surface-muted': '#EEEEEE',
  'fg': '#111111',
  'fg-secondary': 'rgba(17, 17, 17, 0.6)',
  'edge': '#DDDDDD',
  'edge-strong': '#EAEAEA',
  'surface-brand': '#fb2f57',
};

const DARK_DEFAULTS = {
  'primary-500': '#fb2f57',
  'primary-300': '#ff7a91',
  'surface': '#111111',
  'surface-secondary': '#1a1a1a',
  'surface-muted': '#2a2a2a',
  'fg': '#e4e4e7',
  'fg-secondary': 'rgba(228, 228, 231, 0.7)',
  'edge': '#333333',
  'edge-strong': '#222222',
  'surface-brand': '#fb2f57',
};

function get(theme, key) {
  if (theme.dark_only) {
    return theme.dark_colors?.[key] || theme.colors?.[key] || DARK_DEFAULTS[key];
  }
  return theme.colors?.[key] || DEFAULTS[key];
}

function ThemeCard({ theme, isActive, onSelect, isSubmitting }) {
  const bg = get(theme, 'surface-secondary');
  const textColor = get(theme, 'fg');
  const subtextColor = get(theme, 'fg-secondary');
  const borderColor = get(theme, 'edge-strong');
  const brandColor = get(theme, 'surface-brand');

  const swatches = [
    get(theme, 'primary-300'),
    get(theme, 'primary-500'),
    get(theme, 'surface'),
    get(theme, 'surface-muted'),
    get(theme, 'fg'),
  ];

  return (
    <button
      type="button"
      disabled={isSubmitting}
      onClick={() => !isActive && onSelect(theme.slug)}
      className={cn(
        'relative flex flex-col rounded-xl border-2 p-5 text-left transition-all',
        isActive
          ? 'ring-2'
          : 'cursor-pointer',
        isSubmitting && 'opacity-60 pointer-events-none',
      )}
      style={{
        backgroundColor: bg,
        borderColor: isActive ? brandColor : borderColor,
        '--tw-ring-color': isActive ? `${brandColor}40` : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-semibold" style={{ color: textColor }}>
          {theme.name}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {theme.dark_only && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ backgroundColor: get(theme, 'surface-muted'), color: subtextColor }}
            >
              <Moon size={11} strokeWidth={2} />
              Dark only
            </span>
          )}
          {!theme.supports_dark && !theme.dark_only && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ backgroundColor: get(theme, 'surface-muted'), color: subtextColor }}
            >
              <Sun size={11} strokeWidth={2} />
              Light only
            </span>
          )}
          {isActive && (
            <Badge variant="success" size="sm">
              <Check size={12} strokeWidth={2} className="mr-1" />
              Active
            </Badge>
          )}
        </div>
      </div>
      <span className="mt-1 text-sm" style={{ color: subtextColor }}>
        {theme.description}
      </span>

      <div className="mt-4 flex items-center gap-2">
        {swatches.map((color, i) => (
          <div
            key={i}
            className="w-6 h-6 rounded-full"
            style={{ backgroundColor: color, border: `1px solid ${borderColor}` }}
          />
        ))}
      </div>
    </button>
  );
}

export default function Theme({ themes, active_theme }) {
  const [submitting, setSubmitting] = useState(null);

  function handleSelect(slug) {
    setSubmitting(slug);
    router.post(
      admin_settings_theme_settings_path(),
      { slug },
      { onFinish: () => setSubmitting(null) },
    );
  }

  return (
    <SettingsLayout>
      <PageHeader
        title="Theme"
        description="Choose a color palette for your affiliate portal."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {themes.map((theme) => (
          <ThemeCard
            key={theme.slug}
            theme={theme}
            isActive={theme.slug === active_theme}
            onSelect={handleSelect}
            isSubmitting={submitting === theme.slug}
          />
        ))}
      </div>
    </SettingsLayout>
  );
}
